import { useState } from "react";
import PageHero from "@/components/layout/PageHero";
import Breadcrumbs from "@/components/layout/Breadcrumbs";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Mail, MapPin, Phone } from "lucide-react";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/contact", form);
      toast.success(data.message || "Message received");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div data-testid="contact-page">
      <PageHero kicker="Contact" title="We'd love to hear from you" subtitle="Travel question, partnership pitch or feedback — drop us a line and we'll respond within a working day." />
      <div className="container-page py-10">
        <Breadcrumbs items={[{ label: "Contact" }]} />
        <div className="grid md:grid-cols-3 gap-8">
          <div className="md:col-span-2">
            <form onSubmit={submit} className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8 space-y-4" data-testid="contact-form">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Name</label>
                  <input required value={form.name} onChange={set("name")} data-testid="contact-name" className="mt-1 w-full h-11 px-3 rounded-lg border border-slate-200 focus:outline-none focus:border-brand text-sm" />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Email</label>
                  <input required type="email" value={form.email} onChange={set("email")} data-testid="contact-email" className="mt-1 w-full h-11 px-3 rounded-lg border border-slate-200 focus:outline-none focus:border-brand text-sm" />
                </div>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Subject</label>
                <input required value={form.subject} onChange={set("subject")} data-testid="contact-subject" className="mt-1 w-full h-11 px-3 rounded-lg border border-slate-200 focus:outline-none focus:border-brand text-sm" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Message</label>
                <textarea required value={form.message} onChange={set("message")} data-testid="contact-message" rows={6} className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 focus:outline-none focus:border-brand text-sm" />
              </div>
              <button disabled={busy} data-testid="contact-submit" className="h-12 px-6 rounded-lg btn-accent font-semibold uppercase tracking-wider text-sm disabled:opacity-60">
                {busy ? "Sending..." : "Send Message"}
              </button>
            </form>
          </div>
          <aside className="space-y-4">
            <div className="bg-brand text-white rounded-2xl p-6">
              <div className="text-xs uppercase tracking-widest text-white/70 mb-3">Reach us</div>
              <div className="space-y-3 text-sm">
                <div className="flex items-center gap-3"><Mail size={16} className="text-accent" /> hello@mytrippoint.com</div>
                <div className="flex items-center gap-3"><Phone size={16} className="text-accent" /> +91 80 4567 8901</div>
                <div className="flex items-center gap-3"><MapPin size={16} className="text-accent" /> Bengaluru, India</div>
              </div>
            </div>
            <div className="bg-white border border-slate-200 rounded-2xl p-6 text-sm text-slate-600">
              <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Business hours</div>
              Monday – Saturday, 09:00 – 19:00 IST. We respond to emails within one working day.
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
